import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Button,
  Chip,
  Divider,
  Box,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";
import LinkIcon from "@material-ui/icons/Link";
import { useDispatch, useSelector } from "react-redux";
import { submit } from "redux-form";
import FieldForm from "../../forms/admin/FieldForm";
import { useAuthState } from "../../../context/auth";
import { Client, listClients } from "../../../api/clients";
import { ServerMetric, searchMetrics } from "../../../api/metrics";
import Card from "../../cards/Card";
import { handleRequestError, triggerToast } from "../../../helpers";
import { listLoggers, Logger } from "../../../api/loggers";
import LoggersList from "../../elements/admin/LoggersList";
import {
  Field,
  saveField,
  updateField,
  getField as fetchField,
  hydrateProfileParams,
} from "../../../api/fields";
import {
  QrPairing,
  listQrCodes,
  createQrPairing,
  deleteQrPairing,
} from "../../../api/qrCodes";
import Loader from "../../elements/Loader";
import Headline from "../../elements/Headline";
import FormButton from "../../forms/FormButton";
import { useHistory } from "react-router-dom";
import { useStyles } from "./Admin";
import QrScannerDialog from "../../elements/QrScannerDialog";

// Build the editor's initialValues for an existing field: start from the cached
// clients payload (so all scalar fields / sensors are present), then overlay
// each rootzone profile's parameter values from the single-field endpoint —
// the clients list returns profiles without them, so without this the per-
// profile FC/refill/trigger/SMD inputs come back blank. Falls back to the
// cached profiles if that fetch fails. Returns undefined when the field isn't
// found. Resolved before the form mounts so initialValues are complete (the
// form has no enableReinitialize).
async function buildFieldForEdit(
  token: string,
  clientsData: Client[],
  id: number
): Promise<Partial<Field> | undefined> {
  const cached = clientsData
    .flatMap((client) => client.fields ?? [])
    .find((clientField) => clientField.id === id);
  if (!cached) return undefined;

  const { sensors, ...restOfField } = cached;
  const base: any =
    sensors && sensors.length
      ? {
          ...restOfField,
          sensors: sensors.map((sensor) => {
            const { config, ...restOfSensor } = sensor;
            return config
              ? { ...restOfSensor, ...JSON.parse(config) }
              : sensor;
          }),
        }
      : cached;

  let rootzone_profiles = base.rootzone_profiles;
  if (cached.client_id != null) {
    try {
      const res = await fetchField(token, cached.client_id, id);
      const enriched = hydrateProfileParams(res?.data?.rootzone_profiles);
      if (enriched) rootzone_profiles = enriched;
    } catch {
      // keep cached profiles — values just won't prefill
    }
  }

  return { ...base, rootzone_profiles };
}

const ManageField = ({
  id,
  setIsLoading,
}: {
  id: number | string;
  setIsLoading(newValue: boolean): void;
}) => {
  const classes = useStyles();
  const user = useAuthState();
  const history = useHistory();
  const dispatch = useDispatch();
  const numericId = Number(id);
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [metrics, setMetrics] = useState<ServerMetric[]>([]);
  const [loggers, setLoggers] = useState<Logger[]>([]);
  const [field, setField] = useState<Partial<Field>>();
  const [qrCodes, setQrCodes] = useState<QrPairing[]>([]);
  const [newQrContent, setNewQrContent] = useState("");
  const [newQrLabel, setNewQrLabel] = useState("");
  const [showQrScanner, setShowQrScanner] = useState(false);
  const qrSummaryLabel =
    qrCodes.length === 1 ? "1 code paired" : `${qrCodes.length} codes paired`;

  // Get meta data (and, when editing, the field itself before the form mounts)
  useEffect(() => {
    if (!user.token) return;
    const token = user.token;
    setLoading(true);
    Promise.all([
      listClients(token),
      searchMetrics({ raw: 1, modules: [3, 4] }, token),
      listLoggers(token),
    ])
      .then(async ([tempclients, tempmetrics, temploggers]) => {
        setClients(tempclients.data);
        setMetrics(tempmetrics.data.sort((a, b) => (a.ref > b.ref ? 1 : -1)));
        setLoggers(temploggers.data);
        if (numericId) {
          setField(await buildFieldForEdit(token, tempclients.data, numericId));
        }
        setLoading(false);
      })
      .catch((e) => {
        handleRequestError(e, "Failed fetching data: ");
      });
  }, [user.token, numericId]);

  useEffect(() => {
    setIsLoading(loading);
  }, [setIsLoading, loading]);

  const clientsOptions = useMemo(
    () =>
      clients.reduce((obj, client) => {
        if (client.id != null) {
          obj[client.id] = client.name;
        }
        return obj;
      }, {} as Record<NonNullable<Client["id"]>, Client["name"]>),
    [clients]
  );
  // -------------

  // QR Codes
  const loadQrCodes = useCallback(async () => {
    if (!user.token || !field?.client_id) return;
    try {
      const res = await listQrCodes(user.token, field.client_id);
      setQrCodes(res.data.filter((qr: QrPairing) => qr.field_id === numericId));
    } catch {
      // silently fail — non-critical
    }
  }, [user.token, field?.client_id, numericId]);

  useEffect(() => {
    if (field?.client_id) loadQrCodes();
  }, [field?.client_id, loadQrCodes]);

  const handleAddQrCode = async () => {
    if (!user.token || !field?.client_id || !newQrContent.trim()) return;
    try {
      await createQrPairing(user.token, field.client_id, {
        content: newQrContent.trim(),
        field_id: numericId,
        label: newQrLabel.trim() || undefined,
      });
      setNewQrContent("");
      setNewQrLabel("");
      loadQrCodes();
      dispatch(
        triggerToast("QR code paired successfully", { variant: "success" })
      );
    } catch (e) {
      handleRequestError(e, "Failed to pair QR code: ");
    }
  };

  const handleDeleteQrCode = async (qrId: number) => {
    if (!user.token || !field?.client_id) return;
    const confirmed = window.confirm(
      "Remove this QR code pairing from the field?"
    );
    if (!confirmed) return;
    try {
      await deleteQrPairing(user.token, field.client_id, qrId);
      loadQrCodes();
      dispatch(triggerToast("QR code pairing removed", { variant: "success" }));
    } catch (e) {
      handleRequestError(e, "Failed to remove QR code: ");
    }
  };
  // -------------

  // Populate Form & related loggers
  const formValues = useSelector(
    ({ form: { FieldForm } }: any) => FieldForm && FieldForm.values
  );
  const { sensors }: { sensors: { logger_id: number }[] } = formValues || [];
  const fieldLoggers = useMemo(() => {
    if (!loggers || !sensors) return [];
    const loggerIds = sensors.map((item) => item.logger_id).filter((id) => id);
    return loggers.filter((item) => loggerIds.includes(item.id));
  }, [loggers, sensors]);
  // -------------

  const handleSubmit = async (formValues: any) => {
    if (!user.token) return;
    setWaiting(true);
    try {
      const {
        id,
        client_id,
        name,
        farm,
        field_type,
        crop_type,
        valve_group,
        auto_drain_facility,
        runoff_multiplication_factor,
        chart_runoff_multiplication_factor,
        ec_division_factor,
        conductivity_line,
        metric_labels,
        timezone,
        sensors,
        import_frequency,
        rootzone_profiles,
      } = formValues;

      const values = {
        name,
        farm,
        field_type,
        crop_type,
        valve_group,
        auto_drain_facility,
        runoff_multiplication_factor: parseFloat(runoff_multiplication_factor),
        chart_runoff_multiplication_factor: parseFloat(
          chart_runoff_multiplication_factor
        ),
        ec_division_factor: parseFloat(ec_division_factor),
        conductivity_line,
        metric_labels,
        timezone,
        sensors,
        import: formValues.import || false,
        import_frequency,
        rootzone_profiles,
      };
      const response = id
        ? await updateField(user.token, client_id, id, values, field as any)
        : await saveField(user.token, client_id, values);
      dispatch(triggerToast(response.message, { variant: "success" }));
      setWaiting(false);
      history.push("/admin");
    } catch (e) {
      setWaiting(false);
      handleRequestError(e, `Failed to ${id ? "update" : "save"} Field: `);
    }
  };

  return (
    <Loader active={loading} showChildren={false} message={`Loading Field...`}>
      <Headline headline={`Edit Field`} headlineVariant="h1" divider={true}>
        <FormButton
          text={id ? "Update" : "Save"}
          disabled={!valid || waiting}
          handleClick={() => dispatch(submit("FieldForm"))}
          variant="contained"
          color="primary"
          waiting={waiting}
          waitingText={id ? "Updating" : "Saving"}
        />
      </Headline>
      <Grid container spacing={3} justifyContent="space-between">
        <Grid item xs={12} lg={7}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                color="textSecondary"
                className={classes.subTitle}
              >
                DETAILS:
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <FieldForm
                  // @ts-expect-error loggers prop not currently defined in js component
                  loggers={loggers}
                  metrics={metrics}
                  initialValues={field}
                  clients={clientsOptions}
                  isValid={(status: boolean) => setValid(status)}
                  isWaiting={(status: boolean) => setWaiting(status)}
                  onSubmit={handleSubmit}
                />
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                color="textSecondary"
                className={classes.subTitle}
              >
                LOGGERS:
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <LoggersList loggers={fieldLoggers} />
            </Grid>
            {numericId ? (
              <>
                <Grid item xs={12} style={{ marginTop: 24 }}>
                  <Typography
                    variant="h6"
                    color="textSecondary"
                    className={classes.subTitle}
                  >
                    QR CODES:
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gridGap={8}
                      mb={2}
                    >
                      <Box>
                        <Typography variant="subtitle1">
                          Custom QR Pairings
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Pair printed or third-party QR codes to open this
                          field directly.
                        </Typography>
                      </Box>
                      <Chip
                        color={qrCodes.length > 0 ? "primary" : "default"}
                        label={qrSummaryLabel}
                        size="small"
                      />
                    </Box>

                    <Grid container spacing={1} style={{ marginBottom: 8 }}>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          startIcon={<CenterFocusStrongIcon />}
                          onClick={() => setShowQrScanner(true)}
                        >
                          Scan
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          startIcon={<LinkIcon />}
                          disabled={!newQrContent.trim()}
                          onClick={handleAddQrCode}
                        >
                          Pair Typed Code
                        </Button>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1} style={{ marginBottom: 16 }}>
                      <Grid item xs={12}>
                        <TextField
                          label="QR Code Content"
                          placeholder="Paste or type the exact QR payload"
                          value={newQrContent}
                          onChange={(e) => setNewQrContent(e.target.value)}
                          fullWidth
                          size="small"
                          variant="outlined"
                          helperText="Use this when you have the raw QR content and do not want to use the camera."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Label (optional)"
                          placeholder="Friendly name for this code"
                          value={newQrLabel}
                          onChange={(e) => setNewQrLabel(e.target.value)}
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>

                    <Divider />

                    {qrCodes.length > 0 ? (
                      <List dense style={{ marginTop: 8 }}>
                        {qrCodes.map((qr) => (
                          <ListItem key={qr.id}>
                            <ListItemText
                              primary={
                                qr.label ? (
                                  <Typography variant="body2">
                                    <strong>{qr.label}</strong>
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    style={{
                                      color: "rgba(0, 0, 0, 0.6)",
                                      fontFamily:
                                        "Menlo, Monaco, Consolas, monospace",
                                      fontSize: "0.8rem",
                                      lineHeight: 1.5,
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {qr.content}
                                  </Typography>
                                )
                              }
                              secondary={
                                qr.label ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "rgba(0, 0, 0, 0.6)",
                                      display: "block",
                                      fontFamily:
                                        "Menlo, Monaco, Consolas, monospace",
                                      fontSize: "0.78rem",
                                      lineHeight: 1.5,
                                      marginTop: 4,
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {qr.content}
                                  </Typography>
                                ) : null
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleDeleteQrCode(qr.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box py={3}>
                        <Typography variant="body2" color="textSecondary">
                          No QR codes paired to this field yet.
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          style={{ marginTop: 4 }}
                        >
                          Scan a code with the camera or paste its content above
                          to create the first pairing.
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>
                <QrScannerDialog
                  open={showQrScanner}
                  onClose={() => setShowQrScanner(false)}
                  onFieldFound={() => {}}
                  fixedFieldId={numericId}
                  fixedFieldName={field?.name ?? "Field"}
                  onPairingChange={() => void loadQrCodes()}
                />
              </>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </Loader>
  );
};

export default ManageField;
